/**
 * 表格生成工具类
 * 提供统一的表格创建功能，用于显示搜索结果
 */
export const generateResultTable = {
  /**
   * 创建结果表格模态框
   * @param {Object} options - 配置选项
   * @param {Array} options.data - 表格数据
   * @param {String} options.title - 模态框标题
   * @param {Array} options.columns - 表格列定义
   * @param {Function} options.rowClickHandler - 行点击事件处理函数
   * @param {Object} context - Vue实例上下文，用于访问方法和属性
   * @returns {HTMLElement} 创建的模态框元素
   */
  createResultModal(options, context) {
    // 首先删除已存在的模态框，避免多个表格同时存在
    const existingModal = document.querySelector('.result-modal');
    if (existingModal) {
      document.body.removeChild(existingModal);
    }
    const existingOverlay = document.querySelector('.result-modal-overlay');
    if (existingOverlay) {
      document.body.removeChild(existingOverlay);
    }

    // 创建透明遮罩层
    const overlay = document.createElement('div');
    overlay.className = 'result-modal-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.backgroundColor = 'transparent';
    overlay.style.zIndex = '9998';

    // 创建包含结果表格的模态框
    const modal = document.createElement('div');
    modal.className = 'result-modal';
    modal.style.position = 'fixed';
    modal.style.top = '1%';
    modal.style.left = '50%';
    modal.style.transform = 'translateX(-50%)';
    modal.style.zIndex = '9999';
    modal.style.backgroundColor = 'white';
    modal.style.border = '1px solid #ccc';
    modal.style.borderRadius = '4px';
    modal.style.boxShadow = '0 2px 12px 0 rgba(0, 0, 0, 0.1)';
    modal.style.width = '80%';
    modal.style.maxWidth = '800px';
    modal.style.height = '45vh'; // 使用固定高度
    modal.style.overflow = 'hidden';

    // 模态框头部
    const modalHeader = document.createElement('div');
    modalHeader.style.padding = '15px';
    modalHeader.style.borderBottom = '1px solid #ebeef5';
    modalHeader.style.display = 'flex';
    modalHeader.style.justifyContent = 'space-between';
    modalHeader.style.alignItems = 'center';
    modalHeader.style.flexShrink = '0'; // 防止头部被压缩

    const title = document.createElement('h3');
    title.textContent = options.title;
    title.style.margin = '0';

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.fontSize = '20px';
    closeBtn.style.background = 'none';
    closeBtn.style.border = 'none';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.color = '#909399';

    modalHeader.appendChild(title);
    modalHeader.appendChild(closeBtn);
    modal.appendChild(modalHeader);

    // 模态框内容
    const modalBody = document.createElement('div');
    modalBody.style.padding = '20px';
    modalBody.style.overflow = 'auto'; // 同时支持水平和垂直滚动
    modalBody.style.height = 'calc(100% - 70px)'; // 调整计算方式，为底部预留拖拽条空间
    modalBody.style.boxSizing = 'border-box'; // 确保padding包含在高度内
    modalBody.className = 'result-modal-body';

    // 添加滚动条样式，确保滚动条始终可见（只添加一次）
    if (!document.querySelector('#result-modal-scrollbar-style')) {
      const scrollbarStyle = document.createElement('style');
      scrollbarStyle.id = 'result-modal-scrollbar-style';
      scrollbarStyle.textContent = `
        .result-modal-body::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        .result-modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 5px;
        }
        .result-modal-body::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 5px;
        }
        .result-modal-body::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `;
      document.head.appendChild(scrollbarStyle);
    }

    // 创建表格
    const table = this.createTable(options.data, options.columns, options.rowClickHandler, context);
    
    modalBody.appendChild(table);
    modal.appendChild(modalBody);

    // 添加底部调整高度的拖拽条
    const resizeHandle = document.createElement('div');
    resizeHandle.style.height = '6px';
    resizeHandle.style.backgroundColor = '#f0f0f0'; // 让拖拽条更明显
    resizeHandle.style.cursor = 's-resize';
    resizeHandle.style.width = '100%';
    resizeHandle.style.flexShrink = '0'; // 防止拖拽条被压缩
    
    // 给拖拽条添加视觉提示
    const resizeIcon = document.createElement('div');
    resizeIcon.style.width = '30px';
    resizeIcon.style.height = '2px';
    resizeIcon.style.backgroundColor = '#ccc';
    resizeIcon.style.margin = '2px auto';
    resizeIcon.style.borderRadius = '1px';
    resizeHandle.appendChild(resizeIcon);
    
    modal.appendChild(resizeHandle);
    modal.style.display = 'flex'; // 使用flex布局确保元素正确排列
    modal.style.flexDirection = 'column'; // 垂直排列

    // 添加模态框拖拽功能
    this.addDraggableFunctionality(modal, modalHeader, closeBtn);
    
    // 添加调整高度功能
    this.addResizeFunctionality(modal, resizeHandle, modalBody);

    // 添加到文档
    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    // 关闭模态框
    const closeModal = () => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }
    };

    closeBtn.addEventListener('click', closeModal);

    // 点击遮罩层外部关闭 - 但排除拖拽操作
    overlay.addEventListener('click', (e) => {
      // 检查是否是真实的用户点击事件
      if (!e.isTrusted) return;

      // 检查是否在拖拽或调整大小状态
      if (modal.dataset.isResizing === 'true' || modal.dataset.isDragging === 'true') {
        return;
      }

      closeModal();
    });

    // 阻止点击模态框时触发遮罩层的点击事件
    modal.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    return modal;
  },
  
  /**
   * 为模态框添加调整高度功能
   * @param {HTMLElement} modal - 模态框元素
   * @param {HTMLElement} resizeHandle - 调整大小的手柄元素
   * @param {HTMLElement} modalBody - 内容区域元素
   */
  addResizeFunctionality(modal, resizeHandle, modalBody) {
    let isResizing = false;

    resizeHandle.addEventListener('mousedown', (e) => {
      isResizing = true;
      modal.dataset.isResizing = 'true'; // 设置调整大小状态标志
      e.preventDefault();
      e.stopPropagation();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isResizing) return;

      // 计算新高度
      const modalRect = modal.getBoundingClientRect();
      // 获取视窗高度，确保不会超出屏幕
      const viewportHeight = window.innerHeight;
      // 计算鼠标相对于模态框顶部的位置
      const newHeight = e.clientY - modalRect.top;

      // 设置最小高度和最大高度限制
      const minHeight = 200; // 最小高度200px
      const maxHeight = viewportHeight * 0.9; // 最大高度为视窗的90%

      // 确保新高度在限制范围内
      const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

      // 更新模态框高度
      modal.style.height = `${clampedHeight}px`;

      // 更新内容区域高度 - 使用固定值替代引用modalHeader
      modalBody.style.height = `calc(100% - 70px)`;
    });

    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        // 延迟移除状态标志，避免误触发关闭
        setTimeout(() => {
          if (modal.parentNode) {
            modal.dataset.isResizing = 'false';
          }
        }, 50);
      }
    });
  },

  /**
   * 创建表格元素
   * @param {Array} data - 表格数据
   * @param {Array} columns - 表格列定义
   * @param {Function} rowClickHandler - 行点击事件处理函数
   * @param {Object} context - Vue实例上下文
   * @returns {HTMLElement} 表格元素
   */
  createTable(data, columns, rowClickHandler, context) {
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';

    // 表头
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.style.backgroundColor = '#f5f7fa';

    columns.forEach(col => {
      const th = document.createElement('th');
      th.textContent = col;
      th.style.border = '1px solid #ebeef5';
      th.style.padding = '6px 8px';
      th.style.textAlign = 'center';
      th.style.fontSize = '18px';
      th.style.fontWeight = 'bold';
      th.style.whiteSpace = 'nowrap';
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    // 表格内容
    const tbody = document.createElement('tbody');

    // 遍历所有记录，为每条记录创建行并添加点击事件
    data.forEach((row, index) => {
      const tr = document.createElement('tr');
      tr.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#fafafa';
      tr.style.cursor = 'pointer';
      tr.title = '点击定位到该记录';

      // 为每行添加点击事件
      tr.addEventListener('click', () => {
        // 先滚动到筛选表
        if (context && context.$refs.filteredTableTitle) {
          context.$refs.filteredTableTitle.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // 延迟后定位到对应记录
        setTimeout(() => {
          if (rowClickHandler) {
            rowClickHandler(row, index, context);
          } else if (context && context.highlightAndScrollToIndex) {
            // 默认行为：滚动到对应的记录
            const tableBody = document.querySelector('.custom-table .el-table__body-wrapper');
            if (tableBody && context.filteredData) {
              const rowIndex = context.filteredData.findIndex(item => item === row);
              if (rowIndex !== -1) {
                context.highlightAndScrollToIndex(rowIndex);
              }
            }
          }
        }, 300);
      });

      // 行数据
      const dataCells = this.extractRowData(row, columns);

      dataCells.forEach((cell, cellIndex) => {
        const td = document.createElement('td');
        td.textContent = cell;
        td.style.border = '1px solid #ebeef5';
        td.style.padding = '4px 6px';
        td.style.textAlign = 'center';
        td.style.cursor = 'pointer';
        td.style.maxWidth = '200px';
        td.style.minWidth = '60px';
        td.style.maxHeight = '60px';
        td.style.fontSize = '16px';
        td.style.lineHeight = '1.4';

        // 特殊处理"最低分"列：默认显示滚动条
        const columnName = columns[cellIndex];
        if (columnName === '最低分' || columnName === '组内最低分') {
          td.style.overflow = 'auto';
          td.style.textOverflow = 'clip';
          td.style.whiteSpace = 'normal';
        } else if (columnName === '内容') {
          // 内容列：允许自动换行，完整显示长文本
          td.style.overflow = 'visible';
          td.style.textOverflow = 'clip';
          td.style.whiteSpace = 'normal';
          td.style.wordBreak = 'break-all';
        } else {
          // 其他列：限制高度，长文本显示省略号
          td.style.overflow = 'hidden';
          td.style.textOverflow = 'ellipsis';
          td.style.whiteSpace = 'nowrap';
        }

        // 双击单元格显示完整内容
        td.addEventListener('dblclick', (e) => {
          // 阻止双击时的文本选中
          e.preventDefault();
          window.getSelection().removeAllRanges();

          const columnName = columns[cellIndex];
          this.showCellContent(`${columnName}`, cell);
        });

        // 鼠标悬停提示（有省略号才显示）
        td.addEventListener('mouseenter', () => {
          // 检查内容是否溢出（有省略号）
          // 需要同时检查宽度和高度方向的溢出
          const isOverflowing = td.scrollWidth > td.clientWidth + 1 || td.scrollHeight > td.clientHeight + 1;
          if (!isOverflowing) return;

          // 创建悬停提示框
          const tooltip = document.createElement('div');
          tooltip.className = 'table-cell-tooltip';
          tooltip.style.position = 'fixed';
          tooltip.style.zIndex = '10000';
          tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
          tooltip.style.color = 'white';
          tooltip.style.padding = '8px 12px';
          tooltip.style.borderRadius = '4px';
          tooltip.style.fontSize = '12px';
          tooltip.style.maxWidth = '400px';
          tooltip.style.wordWrap = 'break-word';
          tooltip.style.pointerEvents = 'none';
          tooltip.style.opacity = '0';
          tooltip.style.transition = 'opacity 0.2s';
          tooltip.textContent = cell;

          // 计算位置
          const rect = td.getBoundingClientRect();
          tooltip.style.left = `${rect.left}px`;
          tooltip.style.top = `${rect.bottom + 5}px`;

          document.body.appendChild(tooltip);
          td._tooltip = tooltip;

          // 显示动画
          requestAnimationFrame(() => {
            tooltip.style.opacity = '1';
          });
        });

        // 鼠标离开隐藏提示
        td.addEventListener('mouseleave', () => {
          if (td._tooltip) {
            const tooltip = td._tooltip;
            tooltip.style.opacity = '0';
            setTimeout(() => {
              if (tooltip.parentNode) {
                document.body.removeChild(tooltip);
              }
            }, 200);
            td._tooltip = null;
          }
        });

        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    return table;
  },

  /**
   * 从行数据中提取需要显示的单元格数据
   * @param {Object} row - 行数据对象
   * @param {Array} columns - 列定义
   * @returns {Array} 单元格数据数组
   */
  extractRowData(row, columns) {
    // 根据列定义提取相应的数据
    const dataMap = {
      '序号': (row, index) => row._displayIndex || row._originalIndex || (index + 1),
      '学校名': row => row.school_name || '',
      '学校名称': row => row.school_name || '',
      '年份': row => row.admit_year || '',
      '生源地': row => row.origin || '',
      '批次': row => row.batch || '',
      '科类': row => row.subject || '',
      '专业组': row => row.major_group || '',
      '分数': row => row.l_scr || row.score || row.same_rank_score || '-',
      '专业名称': row => row.major_name || '',
      '专业备注': row => row.remark || '',
      // 新增字段映射，用于招生计划
      '学校代码': row => row.school_code || '',
      '专业代码': row => row.major_code || '',
      '再选科目': row => row.sub_combo || '',
      '计划数': row => row.plan || '',
      '学年': row => row.duration || '',
      '学费': row => row.money || '',
      // 学校信息字段映射
      '排名': row => row.ranking || '',
      '简称': row => row.abbreviation || '',
      '英文名字': row => row.english_name || '',
      '学校省份': row => row.province || '',
      '城市': row => row.city || '',
      '汇总标签': row => row.tags || '',
      '专业评估结果': row => row.major_evaluation_results || '',
      '推荐专业': row => row.recommended_majors || '',
      '创建时间': row => row.established_year || '',
      '女生比例': row => row.female_ratio || '',
      '男生比例': row => row.male_ratio || '',
      '自然类型': row => row.location_type || '',
      '学校类型': row => row.institution_type || '',
      '所属机构': row => row.affiliated || '',
      '简单标签': row => row.simple_tag || '',
      '是否艺术': row => row.is_art || '',
      '是否985': row => row.is_985 || '',
      '是否211': row => row.is_211 || '',
      '是否国重点': row => row.is_national_key || '',
      '是否是私立': row => row.is_private || '',
      '世界排名': row => row.world_ranking || '',
      '排名汇总': row => row.ranking_summary || '',
      '硕士点': row => row.master_programs || '',
      '博士点': row => row.doctoral_programs || '',
      '世界一流': row => row.world_class_discipline || '',
      '一流大学': row => row.first_class_university || '',
      '描述': row => row.description || '',
      '本科or专科': row => row.undergraduate_or_vocational || '',
      '招办电话': row => row.admissions_phone || '',
      '电子邮箱': row => row.email || '',
      '通讯地址': row => row.mailing_address || '',
      '官网': row => row.official_website || '',
      '名人': row => row.notable_alumni || '',
      '就业情况': row => row.employment_statistics || '',
      '图片ID': row => row.image_id || '',
      // 竖排键值对表格字段映射
      '字段': row => row.field || '',
      '内容': row => row.value || ''
    };
  
    return columns.map((col, index) => {
      const extractor = dataMap[col];
      if (typeof extractor === 'function') {
        return extractor(row, index);
      }
      // 如果找不到匹配的提取器，尝试直接从row对象中获取对应的值
      // 这样可以支持动态添加的列和未来可能出现的新字段
      const propertyName = Object.keys(row).find(key => 
        col === key || col.includes(key) || 
        (row[key] !== undefined && row[key] !== null)
      );
      return propertyName ? row[propertyName] : '';
    });
  },

  /**
   * 为模态框添加拖拽功能
   * @param {HTMLElement} modal - 模态框元素
   * @param {HTMLElement} header - 头部元素（拖拽区域）
   * @param {HTMLElement} closeBtn - 关闭按钮
   */
  addDraggableFunctionality(modal, header, closeBtn) {
    let isDragging = false;
    let offsetX, offsetY;

    // 为模态框头部添加拖拽事件监听
    header.style.cursor = 'move';
    header.addEventListener('mousedown', (e) => {
      isDragging = true;
      modal.dataset.isDragging = 'true'; // 设置拖拽状态标志
      const rect = modal.getBoundingClientRect();
      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;

      // 添加样式指示拖拽状态
      modal.style.opacity = '0.9';
      modal.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      // 计算新位置
      const newX = e.clientX - offsetX;
      const newY = e.clientY - offsetY;

      // 应用新位置，移除transform属性以支持直接定位
      modal.style.transform = 'none';
      modal.style.left = `${newX}px`;
      modal.style.top = `${newY}px`;
    });

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        // 延迟移除状态标志，避免误触发关闭
        setTimeout(() => {
          if (modal.parentNode) {
            modal.dataset.isDragging = 'false';
            // 恢复原始样式
            modal.style.opacity = '1';
            modal.style.boxShadow = '0 2px 12px 0 rgba(0, 0, 0, 0.1)';
          }
        }, 50);
      }
    });

    // 确保点击关闭按钮时不触发拖拽
    closeBtn.style.cursor = 'pointer';
    closeBtn.onmousedown = (e) => e.stopPropagation();
  },

  /**
   * 显示单元格完整内容
   * @param {String} title - 标题
   * @param {String} content - 内容
   */
  showCellContent(title, content) {
    // 移除已存在的弹窗
    const existingModal = document.querySelector('.cell-content-modal');
    if (existingModal) {
      document.body.removeChild(existingModal);
    }

    // 创建遮罩层
    const overlay = document.createElement('div');
    overlay.className = 'cell-content-modal-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.backgroundColor = 'transparent';
    overlay.style.zIndex = '9999';

    // 创建模态框
    const modal = document.createElement('div');
    modal.className = 'cell-content-modal';
    modal.style.position = 'fixed';
    modal.style.top = '50%';
    modal.style.left = '50%';
    modal.style.transform = 'translate(-50%, -50%)';
    modal.style.zIndex = '10000';
    modal.style.backgroundColor = 'white';
    modal.style.border = '1px solid #ccc';
    modal.style.borderRadius = '4px';
    modal.style.boxShadow = '0 2px 12px 0 rgba(0, 0, 0, 0.15)';
    modal.style.width = '600px';
    modal.style.maxWidth = '90vw';

    // 创建头部
    const header = document.createElement('div');
    header.style.padding = '15px';
    header.style.borderBottom = '1px solid #ebeef5';
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';

    const titleElement = document.createElement('h3');
    titleElement.textContent = title;
    titleElement.style.margin = '0';
    titleElement.style.fontSize = '16px';

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.fontSize = '24px';
    closeBtn.style.background = 'none';
    closeBtn.style.border = 'none';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.color = '#909399';
    closeBtn.style.padding = '0';
    closeBtn.style.lineHeight = '1';

    header.appendChild(titleElement);
    header.appendChild(closeBtn);
    modal.appendChild(header);

    // 创建内容区域
    const body = document.createElement('div');
    body.style.padding = '20px';
    body.style.maxHeight = '70vh';
    body.style.overflow = 'auto';
    body.style.whiteSpace = 'pre-wrap';
    body.style.wordWrap = 'break-word';
    body.style.fontSize = '14px';
    body.style.lineHeight = '1.6';
    body.textContent = content;
    modal.appendChild(body);

    // 关闭功能
    const closeModal = () => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
    };

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    });

    // 阻止点击模态框时关闭
    modal.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // 添加到文档
    document.body.appendChild(overlay);
    overlay.appendChild(modal);
  }
};